import React, { useState, useEffect } from 'react';

import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Controls } from '../../components/controls/Controls';
import { promiseServiceHandler } from '../../services/basicServiceHandler';
import useToast from '../../hooks/useToast';
import TranslationIntakePlanLocalesTable from './TranslationIntakePlanLocalesTable';
import { useApiClient, convertAxiosErrorToUserFacingMessage } from '../../context/ApiClientContext';
import TranslationPipelineLogTable from './TranslationPipelineLogTable';
import useAuth from '../../hooks/useAuth';
import TranslationCycleSection from './TranslationCycleSection';
import PushContentToProductionSection from './PushContentToProductionSection';

const NUM_LOGS_TO_FETCH = 5;
const ENVIRONMENTS_TO_SHOW_TRANSLATION_UTILITIES = ['local', 'development', 'translation-test'];

export default function TranslationPane() {
  const [intakePlansByLocale, setIntakePlansByLocale] = useState({});
  const [pipelineLogs, setPipelineLogs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const { hasPermissions } = useAuth();

  const {
    toastSetSuccessNotification,
    toastSetErrorNotification,
  } = useToast();

  const apiClient = useApiClient();

  const fetchIntakePlanLocales = () => promiseServiceHandler(
    () => apiClient.getIntakePlansByTranslationLocale(),
    (response) => setIntakePlansByLocale(response.data),
    () => toastSetErrorNotification('Unable to fetch module locales.'),
    () => {},
  );

  const fetchRecentPipelineLogs = () => promiseServiceHandler(
    () => apiClient.getRecentPipelineLogs(NUM_LOGS_TO_FETCH),
    (response) => setPipelineLogs(response.data),
    (error) => toastSetErrorNotification(convertAxiosErrorToUserFacingMessage(error)),
    () => {},
  );

  const hasTranslationPipelinePermissions = hasPermissions({ c2i: ['TranslationPipeline'] });

  const loadAllData = async () => {
    const promises = [fetchIntakePlanLocales()];
    if (hasTranslationPipelinePermissions) {
      promises.push(fetchRecentPipelineLogs());
    }
    try {
      await Promise.all(promises);
    } finally {
      setAllDataLoaded(true);
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadAllData();
      toastSetSuccessNotification('Refreshed');
    } finally {
      setRefreshing(false);
    }
  };

  return !allDataLoaded ? <CircularProgress />
    : (
      <>
        <Box
          sx={{
            p: 2,
            border: '1px solid #c6c6c6',
            borderRadius: '4px',
          }}
        >
          <TranslationIntakePlanLocalesTable intakePlansByLocale={intakePlansByLocale} />
        </Box>
        {ENVIRONMENTS_TO_SHOW_TRANSLATION_UTILITIES.includes(window.env.REACT_APP_ENV)
        && hasTranslationPipelinePermissions && (
        <>
          <TranslationCycleSection refreshLogs={fetchRecentPipelineLogs} />
          <PushContentToProductionSection refreshLogs={fetchRecentPipelineLogs} />
          <br />
          <Box
            sx={{
              pl: 2,
              border: '1px solid #c6c6c6',
              borderRadius: '4px',
              maxWidth: 'fit-content',
            }}
          >
            <div style={{ display: 'flex' }}>
              <Typography variant="h6" my={2}>
                Recent Pipeline Runs
              </Typography>
              <Controls.Button
                onClick={handleRefresh}
                sx={{ margin: '12px' }}
                loading={refreshing}
                text="Refresh"
              />
            </div>
            <TranslationPipelineLogTable pipelineLogs={pipelineLogs} />
          </Box>
        </>
        )}
      </>
    );
}
